import React, {useEffect, useState} from "react"
import {
    useModal,
    useSolutionRepository,
    useLocal,
    useRoomParameters,
    useRoomSetup,
    useSolution,
    useProgress,
    useMessage,
    useHelpCta
} from "../AppContext"
import {apiDataToRoomSetup} from "../Hooks/RoomSetup"
import {dispatchSolutionLoadedEvent} from "../Hooks/SolutionRepository"
import {Modal} from "../Components/Modal"
import {LoadIndicator} from "../Components/LoadIndicator"
import {Button, ButtonRed} from "../Components/Button"
import {Clipboard} from "../Components/Clipboard"
import {Share} from "./SolutionRepository/Share"
import {LodeByCode} from "./SolutionRepository/LodeByCode"
import {InputText} from "../Components/Input"
import {RoomName} from "../Components/RoomName"
import {useRename} from "../Hooks/Rename"
import {MODAL__SOLUTION_LOAD, MODAL__SOLUTION_SAVE} from "../Hooks/Modal"
import {SolutionImage} from "../Helper/SolutionImage"
import {iconBin, iconCheckmark2, iconClose, iconEdit, iconLoad, iconShare} from "../Helper/Icons"
import style from "./SolutionRepository.css"

export const solutionDispatchShare = (code, consult = false) => {
    setTimeout(() => {
        document.dispatchEvent(new CustomEvent('solutionShare', {
            detail: {
                code: code,
                consult: consult
            }
        }))
    }, 100);
}

export const SolutionRepositorySave = () => {
    const message = useMessage()
    const solution = useSolution()
    const roomSetup = useRoomSetup()
    const solutionRepository = useSolutionRepository()
    const local = useLocal()
    const [stateSaveIndicator, setStateSaveIndicator] = useState(true)
    const name = RoomName()

    useEffect(() => {
        const save = async () => {
            try {
                await roomSetup.save()
                await solution.setRoomId(roomSetup.getId())
                await solution.save()
                await solutionRepository.save(solution.getId(), solution.getPlanningIds(), name)
            } catch (error) {
                message.showError(local.__(error.message))
            }
            setStateSaveIndicator(false)
        }

        save().finally()
    }, [])

    return <Modal type={MODAL__SOLUTION_SAVE} tile={local.__("Raum speichern")} userStyle={{width: "600px"}}>
        <div className={style.layout}>
            <ListLabel/>
            <div className={style.list}>
                {stateSaveIndicator && <SaveIndicator/>}
                <SavedSolutions/>
            </div>
            <LodeByCode/>
        </div>
    </Modal>
}

export const SolutionRepositoryLoad = () => {
    const local = useLocal()

    return <Modal type={MODAL__SOLUTION_LOAD} tile={local.__("Raum laden")} userStyle={{width: "600px"}}>
        <div className={style.layout}>
            <ListLabel/>
            <div className={style.list}>
                <SavedSolutions/>
            </div>
            <LodeByCode/>
        </div>
    </Modal>
}

const ListLabel = () => {
    const local = useLocal()
    const solutionRepository = useSolutionRepository()

    return solutionRepository.count()
        ? <div className={style.listLabel}>{local.__("Ihre gespeicherten Räume")} ({solutionRepository.count()})</div>
        : ''
}

const SavedSolutions = () => {
    const solutionRepository = useSolutionRepository()

    return solutionRepository.getSolutions().map((savedSolution) => {
        return <Item key={savedSolution.code} savedSolution={savedSolution}/>
    })
}

const Item = ({savedSolution}) => {
    const helpCta = useHelpCta()
    const message = useMessage()
    const modal = useModal()
    const progress = useProgress()
    const roomParameters = useRoomParameters()
    const roomSetup = useRoomSetup()
    const solutionRepository = useSolutionRepository()
    const solution = useSolution()
    const rename = useRename()
    const local = useLocal()
    const [share, setShare] = useState(false)
    const imageUrl = `url('${savedSolution?.image ?? SolutionImage}')`

    const openShare = (event) => {
        if (savedSolution.code == event.detail.code) {
            setShare(true)
        }
    }

    useEffect(() => {
        document.addEventListener("solutionShare", openShare);
        return () => {
            document.removeEventListener("solutionShare", openShare);
        }
    }, [])

    const onLoad = () => {
        const load = async () => {
            try {
                const loadedRoom = await solutionRepository.load(savedSolution.code)
                roomSetup.setRoom(apiDataToRoomSetup(loadedRoom.plannings[0].room, roomParameters.getRoomParameters().interiors))
                solution.setPlannings(loadedRoom.plannings[0].room.id, loadedRoom.plannings)
                modal.close()
                helpCta.disable()

                setTimeout(() => {
                    progress.gotoSolution()
                    dispatchSolutionLoadedEvent()
                }, 100)
            } catch (error) {
                message.showError(local.__(error.message))
            }
        }
        message.showPrompt(local.__("Möchten Sie den aktuellen Raum verwerfen und den Raum %roomCode% laden?", {roomCode: savedSolution.code}), load)
    }

    const onShare = () => {
        setShare(preShare => !preShare)
    }

    const onRemove = () => {
        const remove = () => {
            solutionRepository.remove(savedSolution.code)
        }
        message.showPrompt(local.__("Möchten Sie den Raum %roomCode% löschen?", {roomCode: savedSolution.code}), remove)
    }

    const editName = () => {
        rename.start(savedSolution.name)
    }

    const applyName = () => {
        solutionRepository.setName(savedSolution.code, rename.getValue())
        rename.stop()
    }

    return <div className={style.item}>
        <div className={style.base}>
            <div className={style.image} style={{backgroundImage: imageUrl}}/>
            <div className={style.info}>
                <div>
                    <div className={style.code}>
                        <div>{savedSolution.code}</div>
                        <Clipboard code={savedSolution.code}/>
                    </div>
                    <div className={style.name}>
                        {!rename.isEdit() && <>
                            <div>{savedSolution.name}</div>
                            <span onClick={editName}>{iconEdit}</span>
                        </>}
                        {rename.isEdit() && <>
                            <InputText value={rename.getValue()} onChange={rename.setValue}/>
                            <span onClick={applyName}>{iconCheckmark2}</span>
                            <span onClick={rename.stop}>{iconClose}</span>
                        </>}
                    </div>
                </div>
                <div>
                    <div className={style.count}>
                        {savedSolution.planningCount === 1 && local.__("%planningCount% Planung", {planningCount: savedSolution.planningCount})}
                        {savedSolution.planningCount !== 1 && local.__("%planningCount% Planungen", {planningCount: savedSolution.planningCount})}
                    </div>
                    <div className={style.date}>{local.datetime(savedSolution.datetime)}</div>
                </div>
            </div>
            <div className={style.buttonSet}>
                <div>
                    {local.__("Raum laden")}
                    <Button onClick={onLoad}>{iconLoad}</Button>
                </div>
                <div>
                    {local.__("Raum teilen")}
                    <Button onClick={onShare}>{iconShare}</Button>
                </div>
                <div>
                    {local.__("Raum lösche")}
                    <ButtonRed onClick={onRemove}>{iconBin}</ButtonRed>
                </div>
            </div>
        </div>
        <div style={{maxHeight: share ? "400px" : "0px", overflow: "hidden", transition: "300ms"}}>
            <Share planningCode={savedSolution.code} planningName={savedSolution.name}/>
        </div>
    </div>
}


const SaveIndicator = () => {
    const local = useLocal()

    return <div className={style.save}>
        <LoadIndicator/>
        {local.__("Speicher")}
    </div>
}
